import { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import AuthContext from "../../store/auth-context";
import Constants from "../../../../Constants.json";
import useInvelpsTheme from "../../../../hooks/useInvelpsTheme";

const C = { green: "#6f8a5b", amber: "#caa85e", red: "#c6523f", gold: "#b59a5b" };

const LIGHT = {
  appBg: "#f2ede3",
  paper: "rgba(255,255,255,.94)",
  paperAlt: "#f7f5f0",
  ink: "#18221e",
  muted: "#7a8a82",
  line: "rgba(30,50,38,.09)",
  line2: "rgba(30,50,38,.05)",
  rowH: "rgba(181,154,91,.09)",
};

const DARK = {
  appBg: "#000000",
  paper: "rgba(14,18,16,.96)",
  paperAlt: "rgba(22,28,24,.88)",
  ink: "#e8ede9",
  muted: "#8a9a92",
  line: "rgba(148,163,184,.22)",
  line2: "rgba(148,163,184,.10)",
  rowH: "rgba(181,154,91,.10)",
};

const inputStyle = {
  flex: 1,
  background: "none",
  border: "none",
  outline: "none",
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "inherit",
};

const eyeBtn = {
  background: "none",
  border: "none",
  fontSize: 11,
  cursor: "pointer",
  flexShrink: 0,
  padding: 0,
  fontFamily: "inherit",
};

const linkBtn = {
  background: "none",
  border: "none",
  color: C.gold,
  fontWeight: 800,
  fontSize: 11,
  cursor: "pointer",
  padding: 0,
  fontFamily: "inherit",
  textDecoration: "none",
};

function Field({ label, th, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: ".1em",
          color: th.muted,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function FieldWrap({ children, th, borderColor }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        borderRadius: 11,
        border: `1px solid ${borderColor || th.line}`,
        background: th.paperAlt,
        padding: "0 14px",
        height: 44,
      }}
    >
      {children}
    </div>
  );
}

function FieldIcon({ children }) {
  return <span style={{ fontSize: 13, color: "var(--muted)", flexShrink: 0 }}>{children}</span>;
}

function OAuthBtn({ icon, label, bg, href, th }) {
  return (
    <a
      href={href}
      style={{
        height: 40,
        borderRadius: 10,
        border: `1px solid ${th.line}`,
        background: th.paperAlt,
        fontSize: 12,
        fontWeight: 700,
        color: th.ink,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        textDecoration: "none",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = th.rowH;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = th.paperAlt;
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: 4,
          background: bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 9,
          fontWeight: 900,
          color: "#fff",
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      {label}
    </a>
  );
}

export default function InvelpsLogin({
  googleAuthUrl = "/auth/google",
  linkedinAuthUrl = "/auth/linkedin",
}) {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const { darkMode, setDarkMode } = useInvelpsTheme();
  const isDark = darkMode;
  const th = isDark ? DARK : LIGHT;

  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    document.body.style.background = th.appBg;
    document.body.style.color = th.ink;
  }, [isDark, th.appBg, th.ink]);

  const reset = (nextMode) => {
    setMode(nextMode);
    setMsg(null);
    setPwd("");
    setPwd2("");
  };

  const validate = () => {
    if (!email.trim()) return "Adresse e-mail requise.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Adresse e-mail invalide.";
    if (mode !== "forgot" && !pwd) return "Mot de passe requis.";
    if (mode === "signup" && pwd.length < 8) return "Le mot de passe doit contenir au moins 8 caractères.";
    if (mode === "signup" && pwd !== pwd2) return "Les mots de passe ne correspondent pas.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      setMsg({ type: "err", text: err });
      return;
    }
    if (mode === "forgot") {
      navigate("/forgotpassword");
      return;
    }
    if (mode === "signup") {
      navigate("/signup");
      return;
    }

    setLoading(true);
    setMsg(null);
    try {
      const base = (Constants.BACKEND_SERVER_BASE_URL || "").replace(/\/$/, "");
      const res = await fetch(`${base}/token`, {
        method: "POST",
        body: JSON.stringify({ username: email.trim(), password: pwd }),
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || data?.error || "Identifiants incorrects.");
      const expirationTime = new Date(new Date().getTime() + 86400 * 1000);
      authCtx.login(data.access, data.refresh, data.role, expirationTime.toISOString());
      setMsg({ type: "ok", text: "Connexion réussie. Redirection…" });
      setTimeout(() => navigate("/capital"), 600);
    } catch (e) {
      setMsg({ type: "err", text: e?.message || "Erreur de connexion." });
    } finally {
      setLoading(false);
    }
  };

  const LABEL = {
    login: {
      title: "Connexion",
      sub: "Accédez à votre espace d'analyse",
      cta: "Se connecter",
      link1: "Pas encore de compte ?",
      link1a: "Créer un compte",
      link2: "Mot de passe oublié ?",
    },
    signup: {
      title: "Créer un compte",
      sub: "Rejoignez la plateforme Invelps",
      cta: "Créer mon compte",
      link1: "Déjà un compte ?",
      link1a: "Se connecter",
      link2: null,
    },
    forgot: {
      title: "Mot de passe oublié",
      sub: "Récupérez l'accès à votre compte",
      cta: "Envoyer le lien",
      link1: "Retour à la",
      link1a: "connexion",
      link2: null,
    },
  }[mode];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          background: isDark
            ? `radial-gradient(700px 300px at 50% -5%, rgba(181,154,91,.08), transparent 55%), ${th.appBg}`
            : `radial-gradient(700px 280px at 50% -5%, rgba(181,154,91,.14), transparent 55%), ${th.appBg}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 16px",
        }}
      >
        <button
          type="button"
          onClick={() => setDarkMode(!isDark)}
          title={isDark ? "Mode jour" : "Mode nuit"}
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            zIndex: 200,
            width: 38,
            height: 38,
            borderRadius: 10,
            border: `1px solid ${isDark ? "rgba(148,163,184,.28)" : "rgba(30,50,38,.12)"}`,
            background: isDark ? "rgba(14,18,16,.85)" : "rgba(255,255,255,.80)",
            backdropFilter: "blur(8px)",
            color: th.ink,
            fontSize: 16,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isDark ? "☀" : "☾"}
        </button>

        <div
          style={{
            width: "100%",
            maxWidth: 420,
            borderRadius: 24,
            border: `1px solid ${th.line}`,
            background: th.paper,
            boxShadow: isDark ? "0 24px 64px rgba(0,0,0,.55)" : "0 24px 64px rgba(16,36,24,.13)",
            overflow: "hidden",
            animation: "fadeUp .35s ease both",
          }}
        >
          <div
            style={{
              background: isDark
                ? "linear-gradient(135deg,#1a3329 0%,#0f2018 100%)"
                : "linear-gradient(135deg,#2a4f3f 0%,#1e3a2e 100%)",
              padding: "36px 36px 32px",
              textAlign: "center",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                opacity: 0.04,
                pointerEvents: "none",
                backgroundImage: "radial-gradient(circle at 1px 1px,#fff 1px,transparent 0)",
                backgroundSize: "20px 20px",
              }}
            />
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 20, position: "relative" }}>
              <div style={{ width: 2.5, height: 22, background: C.gold, borderRadius: 2 }} />
              <span
                style={{
                  fontFamily: "'Cormorant Garamond',Georgia,serif",
                  fontSize: 20,
                  fontWeight: 700,
                  color: C.gold,
                  letterSpacing: ".14em",
                }}
              >
                INVELPS
              </span>
            </div>
            <div
              style={{
                fontSize: "clamp(18px,3vw,24px)",
                fontWeight: 700,
                color: "#fff",
                fontFamily: "'Cormorant Garamond',Georgia,serif",
                marginBottom: 6,
                position: "relative",
              }}
            >
              {LABEL.title}
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", fontWeight: 600, position: "relative" }}>
              {LABEL.sub}
            </div>
          </div>

          <div style={{ padding: "32px 36px 28px" }}>
            {msg && (
              <div
                style={{
                  borderRadius: 10,
                  padding: "10px 14px",
                  marginBottom: 16,
                  fontSize: 12,
                  fontWeight: 600,
                  background: msg.type === "ok" ? `${C.green}18` : `${C.red}14`,
                  border: `1px solid ${msg.type === "ok" ? C.green : C.red}44`,
                  color: msg.type === "ok" ? C.green : C.red,
                  animation: "fadeIn .2s ease",
                }}
              >
                {msg.type === "ok" ? "✓ " : "✕ "}
                {msg.text}
              </div>
            )}

            <Field label="Adresse e-mail" th={th}>
              <FieldWrap th={th}>
                <FieldIcon>✉</FieldIcon>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                  placeholder="email@exemple.com"
                  type="email"
                  style={{ ...inputStyle, color: th.ink }}
                />
              </FieldWrap>
            </Field>

            {mode !== "forgot" && (
              <Field label="Mot de passe" th={th}>
                <FieldWrap th={th}>
                  <FieldIcon>◈</FieldIcon>
                  <input
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submit()}
                    placeholder={mode === "signup" ? "Minimum 8 caractères" : "Votre mot de passe"}
                    type={showPwd ? "text" : "password"}
                    style={{ ...inputStyle, color: th.ink }}
                  />
                  <button type="button" onClick={() => setShowPwd((s) => !s)} style={{ ...eyeBtn, color: th.muted }}>
                    {showPwd ? "Cacher" : "Voir"}
                  </button>
                </FieldWrap>
              </Field>
            )}

            {mode === "signup" && (
              <Field label="Confirmer le mot de passe" th={th}>
                <FieldWrap th={th} borderColor={pwd2 && pwd2 !== pwd ? C.red : undefined}>
                  <FieldIcon>◈</FieldIcon>
                  <input
                    value={pwd2}
                    onChange={(e) => setPwd2(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submit()}
                    placeholder="Répétez le mot de passe"
                    type={showPwd ? "text" : "password"}
                    style={{ ...inputStyle, color: th.ink }}
                  />
                </FieldWrap>
              </Field>
            )}

            <button
              type="button"
              onClick={submit}
              disabled={loading}
              style={{
                width: "100%",
                height: 46,
                borderRadius: 12,
                border: "none",
                marginTop: 6,
                background: loading ? "rgba(181,154,91,.4)" : `linear-gradient(135deg,${C.gold},#c9a84c)`,
                color: "#fff",
                fontSize: 13,
                fontWeight: 800,
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                boxShadow: loading ? "none" : "0 4px 16px rgba(181,154,91,.35)",
              }}
            >
              {loading ? (
                <>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      border: "2px solid rgba(255,255,255,.4)",
                      borderTopColor: "#fff",
                      borderRadius: 999,
                      display: "inline-block",
                      animation: "spin 0.7s linear infinite",
                    }}
                  />
                  Traitement…
                </>
              ) : (
                LABEL.cta
              )}
            </button>

            <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
              <div style={{ fontSize: 11, color: th.muted, fontWeight: 600 }}>
                {LABEL.link1}{" "}
                {mode === "signup" ? (
                  <button type="button" onClick={() => reset("login")} style={linkBtn}>
                    {LABEL.link1a}
                  </button>
                ) : (
                  <Link to="/signup" style={linkBtn}>
                    {LABEL.link1a}
                  </Link>
                )}
              </div>
              {LABEL.link2 && (
                <Link to="/forgotpassword" style={{ ...linkBtn, color: th.muted, textDecoration: "underline" }}>
                  {LABEL.link2}
                </Link>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "22px 0 16px" }}>
              <div style={{ flex: 1, height: 1, background: th.line }} />
              <span style={{ fontSize: 10, color: th.muted, fontWeight: 700, flexShrink: 0 }}>OU CONTINUER AVEC</span>
              <div style={{ flex: 1, height: 1, background: th.line }} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <OAuthBtn icon="G" label="Google" bg="#4285F4" href={googleAuthUrl} th={th} />
              <OAuthBtn icon="in" label="LinkedIn" bg="#0A66C2" href={linkedinAuthUrl} th={th} />
            </div>
          </div>

          <div style={{ padding: "14px 36px 20px", borderTop: `1px solid ${th.line2}`, textAlign: "center" }}>
            <div style={{ fontSize: 10, color: th.muted, fontWeight: 600, lineHeight: 1.6 }}>
              En continuant, vous acceptez nos{" "}
              <span style={{ color: C.gold, fontWeight: 700, cursor: "pointer" }}>Conditions d'utilisation</span> et notre{" "}
              <span style={{ color: C.gold, fontWeight: 700, cursor: "pointer" }}>Politique de confidentialité</span>.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
